/* eslint-disable max-lines-per-function */
import React, { useContext, useState } from "react";
import Navbar from "../../../../components/navbar/Navbar";
import { Box, Grid, Step, StepLabel, Stepper, Typography } from "@mui/material";
import {
  ECOMMERCE_STEPS,
  INITIAL_CONFIG_STEPS,
  MARKETPLACE,
  ECOMMERCE,
  MEDIUM,
} from "../../../../utils/constants";
import { IStepsByEntity } from "../../../../interfaces/configuration";
import { UserContext } from "../../../../contexts/userContext";
import Loader from "../../../../components/Loader";
import ContractOwner from "../../../../components/configuration/ContractOwner";
import MerchantInvitations from "../../../../components/configuration/MerchantInvitations";
import Rules from "../../../../components/configuration/RulesContainer";
import WebhookSettings from "../../../../components/configuration/WebhookSettings";
import CarriersInitialConfig from "../../../../components/configuration/CarriersInitialConfig";

export interface IStepState {
  completed: boolean;
  skipped: boolean;
  optional: boolean;
}

export interface IConfigurationState {
  currentStep: number;
  steps: Record<number, IStepState>;
  canProceed: boolean;
}

const InitialConfigurationV2 = () => {
  const {
    currentUser: { entity },
  } = useContext(UserContext);

  const getInitialStepsConfig = (): Record<number, IStepState> => {
    if (entity === ECOMMERCE) {
      return {
        0: { completed: false, skipped: false, optional: false }, // Webhook
        1: { completed: false, skipped: false, optional: true }, // Carriers
      };
    }

    return {
      0: { completed: false, skipped: false, optional: false }, // Contract Owner
      1: { completed: false, skipped: false, optional: true }, // Merchant Invitations
      2: { completed: false, skipped: false, optional: true }, // Webhook
      3: { completed: false, skipped: false, optional: true }, // Rules
      4: { completed: false, skipped: false, optional: true }, // Carriers
    };
  };

  const [configState, setConfigState] = useState<IConfigurationState>({
    currentStep: 0,
    steps: getInitialStepsConfig(),
    canProceed: false,
  });

  const handleNext = (skipCurrent = false) => {
    setConfigState((prev) => ({
      ...prev,
      steps: {
        ...prev.steps,
        [prev.currentStep]: {
          ...prev.steps[prev.currentStep],
          completed: !skipCurrent,
          skipped: skipCurrent,
        },
      },
      currentStep: prev.currentStep + 1,
    }));
  };

  const handleBack = () => {
    setConfigState((prev) => ({
      ...prev,
      currentStep: Math.max(0, prev.currentStep - 1),
    }));
  };

  const handleSkip = () => {
    const currentStepConfig = configState.steps[configState.currentStep];
    if (currentStepConfig?.optional) {
      handleNext(true);
    }
  };

  const isStepOptional = (step: number) => configState.steps[step]?.optional;
  const navHandlers = {
    handleBack,
    handleNext: () => handleNext(false),
    handleSkip,
    isOptional: isStepOptional(configState.currentStep),
  };

  const initialConfigMktStepsComponents = [
    <ContractOwner handleNext={() => handleNext(false)} />,
    <MerchantInvitations {...navHandlers} />,
    <WebhookSettings {...navHandlers} />,
    <Rules {...navHandlers} />,
  ];
  const initialConfigEcomStepsComponents = [
    <WebhookSettings {...navHandlers} />,
  ];

  const stepsByEntity: IStepsByEntity = {
    [MARKETPLACE]: {
      steps: INITIAL_CONFIG_STEPS,
      components: initialConfigMktStepsComponents,
    },
    [ECOMMERCE]: {
      steps: ECOMMERCE_STEPS,
      components: initialConfigEcomStepsComponents,
    },
  };

  // Configuración específica para Carriers con información de flete propio
  const carriersConfig = {
    ...navHandlers,
    showOwnFleetInfo: true,
    isLastStep:
      configState.currentStep === stepsByEntity[entity].steps.length - 1,
    infoMessage:
      "Si utilizas flete propio, puedes omitir este paso haciendo clic en configurar más tarde. La configuración se completará automáticamente cuando cargues tu matriz de costos en la sección de configuración.",
  };

  // Now update the components arrays to include CarriersInitialConfig with the correct config
  stepsByEntity[MARKETPLACE].components[4] = <CarriersInitialConfig {...carriersConfig} />;
  stepsByEntity[ECOMMERCE].components[1] = <CarriersInitialConfig {...carriersConfig} />;

  return (
    <>
      <Navbar />
      {entity ? (
        <>
          <Box className="initial-config-container d-flex" mt={8}>
            <Grid container>
              <Grid
                item
                xs={3.5}
                p={5}
                className="initial-config-steps-container"
              >
                <Typography className="color-primary f-s-36 m-b-20">
                  ¡Te damos la bienvenida!
                </Typography>
                <Typography className="f-s-24">
                  Empecemos con la configuración inicial.
                </Typography>
                <Stepper
                  activeStep={configState.currentStep}
                  orientation="vertical"
                  className="m-t-50"
                >
                  {stepsByEntity[entity].steps.map(
                    (step: string, index: number) => {
                      const stepState = configState.steps[index];
                      return (
                        <Step key={step} completed={stepState?.completed}>
                          <StepLabel
                            optional={
                              stepState?.optional ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Opcional
                                </Typography>
                              ) : null
                            }
                          >
                            {step}
                            {stepState?.skipped && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="warning.main"
                                ml={1}
                              >
                                (Omitido)
                              </Typography>
                            )}
                          </StepLabel>
                        </Step>
                      );
                    }
                  )}
                </Stepper>
              </Grid>
              <Grid
                item
                xs={8.5}
                display="flex"
                justifyContent="center"
                alignItems="center"
                paddingY={3}
              >
                {stepsByEntity[entity].components[configState.currentStep]}
              </Grid>
            </Grid>
          </Box>
        </>
      ) : (
        <Loader size={MEDIUM} />
      )}
    </>
  );
};

export default InitialConfigurationV2;
